import { injectIntl, type InjectedIntl, FormattedHTMLMessage } from "react-intl";

import { getSafe } from "@jslib/shared/utils/getSafe";

import { getOption } from "@/options/options";

import {
  type VerificationService,
  type ErrorId,
  type ErrorResponse,
  type ErrorDetailId,
  type ViewModel,
} from "../../lib/types/types";
import { PoweredByComponent } from "../PoweredBy/PoweredByComponent";
import { LogoComponent } from "../LogoComponent/LogoComponent";
import { FEEDBACK_FORM_URL, SUPPORT_URL } from "../../constants";
import { getLocaleSafely } from "../../lib/intl/intl";
import { recordEvent } from "../../lib/GoogleAnalytics/ga";
import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { LinkExternal } from "../LinkExternal";
import { makeFormattedHTMLTagReplacer } from "../../lib/utils/customTagReplacer";
import { TryAgainActionComponent } from "../TryAgainAction";
import { AnnouncingHeader } from "../AnnouncingHeader/AnnouncingHeader";

interface StepErrorProps {
  verificationService: VerificationService;
  errorId?: ErrorId; // eslint-disable-line
  intl?: InjectedIntl; // eslint-disable-line
}

const ErrorTitle = ({
  verificationService,
  errorIds,
}: {
  verificationService: VerificationService;
  errorIds: ErrorId[];
}) => {
  const firstErrorId = errorIds[0];
  const errorMessageId = verificationService?.messages[`step.error.errorId.${firstErrorId}.title`]
    ? `step.error.errorId.${firstErrorId}.title`
    : "error";

  return (
    <AnnouncingHeader align="center">
      <FormattedHTMLMessage id={errorMessageId} defaultMessage="Error" tagName="h1" />
    </AnnouncingHeader>
  );
};

const ErrorMsg = ({
  programId,
  viewModel,
  verificationResponse,
  errorIds,
  errorDetailId,
}: {
  programId: string;
  viewModel: ViewModel;
  verificationResponse: ErrorResponse;
  errorIds: ErrorId[];
  errorDetailId: ErrorDetailId;
}) => {
  const displayErrorMessageIds = new Map();
  if (errorDetailId) {
    displayErrorMessageIds.set(errorDetailId, `errorDetailId.${errorDetailId}`);
  } else {
    errorIds?.forEach((errId) => displayErrorMessageIds.set(errId, `errorId.${errId}`));
  }

  if (displayErrorMessageIds.size > 0) {
    return (
      <p>
        {Array.from(displayErrorMessageIds).map(([errId, errMsgId]) => (
          <FormattedHTMLMessage key={errMsgId} id={errMsgId} defaultMessage={errId}>
            {makeFormattedHTMLTagReplacer({
              // eslint-disable-next-line react/no-unstable-nested-components
              feedbackFormLink: (content) => {
                const href = `${FEEDBACK_FORM_URL}&pid=${programId || ""}&locale=${getLocaleSafely(
                  viewModel,
                  verificationResponse,
                ).replace("-", "_")}`;
                return <LinkExternal href={href}>{content}</LinkExternal>;
              },
              // eslint-disable-next-line react/no-unstable-nested-components
              selfieTipsLink: () => {
                const href = `${SUPPORT_URL}/${getLocaleSafely(
                  viewModel,
                  verificationResponse,
                )}/help-center/selfie-tips`;
                return <LinkExternal href={href}>{href}</LinkExternal>;
              },
              // eslint-disable-next-line react/no-unstable-nested-components
              imageTipsLink: () => {
                const href = `${SUPPORT_URL}/${getLocaleSafely(
                  viewModel,
                  verificationResponse,
                )}/help-center/image-tips`;
                return <LinkExternal href={href}>{href}</LinkExternal>;
              },
            })}
          </FormattedHTMLMessage>
        ))}
      </p>
    );
  }

  return null;
};

const ErrorButton = ({
  verificationService,
  errorIds,
  redirectUrl,
}: {
  verificationService: VerificationService;
  errorIds: ErrorId[];
  redirectUrl?: string;
}) => {
  if (errorIds.includes("unauthorizedAccountStatus")) {
    const faqUrl = getSafe(() => verificationService.programTheme.config.brandInfo.faqUrl);
    if (!faqUrl) return null;
    return (
      <LinkExternal
        href={faqUrl}
        onClick={() => recordEvent(VerificationStepsEnum.error, faqUrl)}
        className="sid-btn sid-faq-btn sid-btn-light"
      >
        <FormattedHTMLMessage
          id="step.error.errorId.unauthorizedAccountStatus.buttonText"
          defaultMessage={
            verificationService.messages["step.error.errorId.unauthorizedAccountStatus.buttonText"]
          }
        />
      </LinkExternal>
    );
  }

  if (errorIds.includes("verificationLimitExceeded")) return null;

  if (
    errorIds.includes("missingRequiredMetadata") ||
    errorIds.includes("missingRequiredExternalId")
  ) {
    if (getOption("tryAgainAction").type === "reset") return null;
    return <TryAgainActionComponent />;
  }

  const firstErrorId = errorIds[0];
  if (
    verificationService.messages?.[`step.error.errorId.${firstErrorId}.buttonText`] &&
    redirectUrl
  ) {
    return (
      <LinkExternal
        href={redirectUrl}
        onClick={() => recordEvent(VerificationStepsEnum.error, redirectUrl)}
        className="sid-btn sid-error-btn sid-btn-light"
      >
        <FormattedHTMLMessage
          id={`step.error.errorId.${firstErrorId}.buttonText`}
          defaultMessage={
            verificationService.messages[`step.error.errorId.${firstErrorId}.buttonText`]
          }
        />
      </LinkExternal>
    );
  }

  return <TryAgainActionComponent />;
};

const StepError = ({ verificationService, errorId }: StepErrorProps) => {
  const verificationResponse = verificationService.verificationResponse as ErrorResponse;
  const { viewModel, programId } = verificationService;
  const { errorIds: responseErrorIds, redirectUrl, errorDetailId } = verificationResponse;

  const errorIdsSet = new Set<ErrorId>();
  if (errorId) errorIdsSet.add(errorId);
  if (Array.isArray(responseErrorIds)) responseErrorIds.forEach((errId) => errorIdsSet.add(errId));
  const errorIds = Array.from(errorIdsSet);

  return (
    <div id="sid-step-error" className="sid-error-container sid-l-container">
      <div className="sid-header sid-l-horz-center" role="banner">
        <div className="sid-error-logo-container sid-l-horz-center">
          <LogoComponent
            verificationService={verificationService}
            fallbackLogo={
              <img
                className="sid-error-logo sid-l-lead-image"
                alt=""
                src="https://assets-resources.sheerid.com/common/images/2018/icons/circle-exclamation.svg"
              />
            }
          />
        </div>
        <ErrorTitle verificationService={verificationService} errorIds={errorIds} />
        <div className="sid-header__subtitle sid-l-horz-center sid-l-space-top-md">
          <div className="sid-error-msg sid-l-horz-center">
            <ErrorMsg
              programId={programId}
              viewModel={viewModel}
              verificationResponse={verificationResponse}
              errorIds={errorIds}
              errorDetailId={errorDetailId}
            />
          </div>
          <div className="sid-l-space-top-lg sid-error-button-container sid-l-horz-center">
            <ErrorButton
              verificationService={verificationService}
              errorIds={errorIds}
              redirectUrl={redirectUrl}
            />
          </div>
        </div>
      </div>

      <div className="sid-footer sid-powered-by-container sid-l-horz-center sid-l-space-top-xl sid-h-small-text">
        <PoweredByComponent verificationService={verificationService} isCentered />
      </div>
    </div>
  );
};

export const StepErrorComponent = injectIntl(StepError);
